import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getPublicTakeHome, savePublicTakeHomeFeedback } from '@/api/takehome';
import { useNoReferrer } from './useNoReferrer';

const QUESTIONS = [
  { field: 'feel', prompt: 'How did making this beat feel?', choices: [['meh', '😕', 'Meh'], ['good', '🙂', 'Good'], ['fire', '🔥', 'Fire']] },
  { field: 'learned', prompt: 'Did you learn something new?', choices: [['no', '', 'No'], ['a_little', '', 'A little'], ['a_lot', '', 'A lot']] },
  { field: 'more', prompt: 'Want to make more music?', choices: [['no', '', 'No'], ['maybe', '', 'Maybe'], ['yes', '', 'Yes']] },
];

const clock = (seconds) => {
  const safe = Math.max(0, Number(seconds) || 0);
  return `${Math.floor(safe / 60)}:${String(Math.floor(safe % 60)).padStart(2, '0')}`;
};

export function TakeHomePlayer({ data, programName = '', expiry = '' }) {
  const audio = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [duration, setDuration] = useState(Number(data.duration_seconds) || 0);

  useEffect(() => {
    setPlaying(false); setElapsed(0); setDuration(Number(data.duration_seconds) || 0);
  }, [data]);

  const toggle = () => {
    if (!audio.current) return;
    if (audio.current.paused) audio.current.play().catch(() => setPlaying(false));
    else audio.current.pause();
  };

  return <section className="public-takehome-player">
    <p>{programName}</p><h1>{data.title}</h1>
    <audio ref={audio} preload="metadata" src={data.stream_url}
      onLoadedMetadata={(event) => setDuration(Number(event.currentTarget.duration) || duration)}
      onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)}
      onTimeUpdate={(event) => setElapsed(event.currentTarget.currentTime)} />
    <button className="public-play" aria-label={playing ? 'Pause beat' : 'Play beat'} onClick={toggle}><span aria-hidden="true">{playing ? 'Ⅱ' : '▶'}</span>{playing ? 'Pause' : 'Play my beat'}</button>
    <div className="public-time"><span>{clock(elapsed)}</span><span>{clock(duration)}</span></div>
    <input aria-label="Beat position" type="range" min="0" max={Math.max(1, duration)} value={Math.min(elapsed, duration)} onChange={(event) => {
      const next = Number(event.target.value); if (audio.current) audio.current.currentTime = next; setElapsed(next);
    }} />
    <a className="public-download" href={data.download_url}>Download MP3</a>
    {expiry && <p className="public-expiry">Download before {expiry}</p>}
    <p className="public-made-with">Made with Let The Beat Build</p>
  </section>;
}

function FeedbackCard({ token, asked }) {
  const [mode, setMode] = useState(asked ? 'thanks' : 'asking');
  const [question, setQuestion] = useState(0);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const answer = async (field, value) => {
    setSaving(true); setError('');
    try {
      await savePublicTakeHomeFeedback(token, { [field]: value });
      if (question === QUESTIONS.length - 1) setMode('thanks');
      else setQuestion((current) => current + 1);
    } catch {
      setError('Your answer could not be saved. You can try again or skip it.');
    } finally { setSaving(false); }
  };
  if (mode === 'closed') return null;
  if (mode === 'thanks') return <section className="public-feedback" aria-label="Optional feedback">
    <p className="public-feedback-thanks">Thanks! Your answers help LTBB.</p>
    <button onClick={() => { setQuestion(0); setError(''); setMode('asking'); }}>Change answers</button>
  </section>;
  const current = QUESTIONS[question];
  return <section className="public-feedback" aria-label="Optional feedback">
    <span>Optional</span>
    <h2>{current.prompt}</h2>
    <div className="public-feedback-choices">
      {current.choices.map(([value, emoji, label]) => <button key={value} disabled={saving} onClick={() => answer(current.field, value)}>
        {emoji && <b aria-hidden="true">{emoji}</b>}<span>{label}</span>
      </button>)}
    </div>
    <button className="public-feedback-skip" onClick={() => setMode('closed')}>Skip</button>
    {error && <p role="alert">{error}</p>}
  </section>;
}

export function TakeHomePage() {
  useNoReferrer();
  const { token = '' } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    getPublicTakeHome(token).then((result) => { if (active) setData(result); })
      .catch((requestError) => { if (active) setError(requestError.message || 'This beat is no longer available. Ask your teacher.'); });
    return () => { active = false; };
  }, [token]);

  const expiry = useMemo(() => {
    if (!data?.expires_at) return '';
    const value = new Date(data.expires_at);
    return Number.isNaN(value.getTime()) ? '' : value.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
  }, [data]);

  if (error) return <main className="public-takehome public-takehome-missing" data-skin="playground">
    <img src="/Pizzul_Hero.png" alt="Let The Beat Build" /><h1>Beat unavailable</h1><p>{error}</p>
  </main>;
  if (!data) return <main className="public-takehome public-takehome-loading" data-skin="playground" role="status">Loading your beat...</main>;

  return <main className="public-takehome" data-skin="playground">
    <header className="public-takehome-brand"><img src="/Pizzul_Hero.png" alt="" /><span>LET THE BEAT BUILD</span></header>
    <TakeHomePlayer data={data} programName={data.program_name} expiry={expiry} />
    <FeedbackCard token={token} asked={Boolean(data.feedback?.asked)} />
  </main>;
}

export default TakeHomePage;
